import { nullifyUndefinedProperties } from 'tradera/utils/object';
import { capitalize } from 'tradera/utils/string';
import {
    SUPPORTED_LANGUAGES,
    DEFAULT_LANGUAGE
} from 'tradera/lang/constants.mjs';
import type { Language } from './types';

const getInitialPreferredLanguage = (
    languageCodeIso2: string,
    availableLanguages: Language[],
    defaultLanguage: Language
) => {
    const availableIsoCodes = availableLanguages.map(
        (lang) => lang.languageCodeIso2
    );
    if (
        !!languageCodeIso2 &&
        availableIsoCodes.indexOf(languageCodeIso2) !== -1
    ) {
        return availableLanguages.find(
            (l) => l.languageCodeIso2 === languageCodeIso2
        );
    }

    return defaultLanguage;
};

const getLanguageDisplayName = (languageCodeIso2: string) =>
    capitalize(
        // Only works on server side, lacks good browser support
        new Intl.DisplayNames([languageCodeIso2], { type: 'language' }).of(
            languageCodeIso2
        )
    );

export const getInitialServerLanguage = ({
    preferredLanguage = DEFAULT_LANGUAGE,
    memberCountryCodeIso2 = null,
    automaticTranslationPreference = false
}) => {
    const defaultLanguage = {
        languageCodeIso2: preferredLanguage,
        displayName: getLanguageDisplayName(preferredLanguage)
    };
    const availableLanguages = SUPPORTED_LANGUAGES.map((languageCodeIso2) => ({
        languageCodeIso2,
        displayName: getLanguageDisplayName(languageCodeIso2)
    }));

    return {
        preferred: getInitialPreferredLanguage(
            preferredLanguage,
            availableLanguages,
            defaultLanguage
        ),
        available: availableLanguages,
        memberCountryCodeIso2,
        automaticTranslationPreference
    };
};

// KATS: this should probably be in a large "InitData" type
type InitDataSubSetForLanguage = {
    defaultLanguage: Language;
    availableLanguages: Language[];
    languageCodeIso2: string;
    memberCountryCodeIso2: string;
    automaticTranslationPreference: boolean;
};

export default (initData: InitDataSubSetForLanguage) => {
    const result = nullifyUndefinedProperties({
        preferred: getInitialPreferredLanguage(
            initData.languageCodeIso2,
            initData.availableLanguages || [],
            initData.defaultLanguage
        ),
        memberCountryCodeIso2: initData.memberCountryCodeIso2,
        available: initData.availableLanguages,
        automaticTranslationPreference: initData.automaticTranslationPreference
    });
    return result;
};
